import { useState, memo, useEffect, useRef } from 'react';

import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';

import MoneyInputBase from '#client/components/BaseMoneyInput';

import WarningMessage from './WarningMessage';

import { validateInvalidValues, validateLimits } from '../utils/validation';

function DepositMoneyInput({
  defaultValue,
  constants,
  remainLimits,
  skipLimitValidate,
  handleDataChange,
  invalidValues = [],
  isDisable = false
}) {
  const inputRef = useRef(null);
  const [state, setState] = useState({
    value: defaultValue,
    isValid: defaultValue > 0,
    isError: false,
    message: null
  });

  const handleChangeValue = (value) => {
    const afterValidateLimits = validateLimits({
      value,
      remainLimits,
      constants,
      skipLimitValidate
    });

    const afterValidateInvalidValues = validateInvalidValues({
      state: afterValidateLimits,
      invalidValues
    });

    // If the new state is equal to the current state, do nothing to prevent re-render
    if (isEqual(afterValidateInvalidValues, state)) {
      return;
    }

    setState(afterValidateInvalidValues);
    handleDataChange(afterValidateInvalidValues);
  };

  useEffect(() => {
    handleChangeValue(state.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!remainLimits.dailyRemainLimit]);

  return (
    <Stack gap={{ xs: '4px', md: '16px' }}>
      <Text
        size="md"
        weight="medium"
      >
        Amount
      </Text>
      <Text
        color="primary"
        size="sm"
        sx={{ mb: '12px' }}
      >
        This number must match the check amount
      </Text>
      <MoneyInputBase
        autoFocus={false}
        defaultValue={defaultValue}
        error={state.isError}
        isDisable={isDisable}
        onChangeValue={handleChangeValue}
        ref={inputRef}
      />
      {state.isError && <WarningMessage message={state.message} />}
    </Stack>
  );
}

DepositMoneyInput.propTypes = {
  defaultValue: PropTypes.number,
  constants: PropTypes.shape({
    dailyLimit: PropTypes.number,
    monthlyLimit: PropTypes.number,
    checkDepositLimit: PropTypes.number
  }).isRequired,
  remainLimits: PropTypes.shape({
    dailyRemainLimit: PropTypes.number,
    monthlyRemainLimit: PropTypes.number,
    checkDepositRemainLimit: PropTypes.number
  }).isRequired,
  skipLimitValidate: PropTypes.bool,
  handleDataChange: PropTypes.func.isRequired,
  invalidValues: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      message: PropTypes.string
    })
  ),
  isDisable: PropTypes.bool
};

export default memo(DepositMoneyInput);
